/**
 * P3.5 — Option-A effective_payee display helpers.
 *
 * Transparent display-layer abstraction over the new
 * `jurisdiction.effective_payee` payload added in P3.4. When the
 * Option-A field is present (e.g., for PA jurisdictions with an
 * active collector assignment) display surfaces read from it. When
 * absent (backend flag off, non-PA jurisdiction, or resolver miss)
 * the helper falls back to the legacy `tax_collector_*` fields so
 * rendered output is byte-identical to pre-P3.5.
 *
 * STRICTLY DISPLAY-ONLY. Editor forms, search inputs, payload
 * assembly, and any code that posts back to the API MUST continue
 * to read/write the legacy `tax_collector_*` fields directly.
 */

const LEGACY_FIELD_BY_PAYEE_KEY = {
  name: 'tax_collector_name',
  make_checks_payable_to: 'make_checks_payable_to',
  mailing_address: 'tax_collector_mailing_address',
  phone: 'tax_collector_phone',
  phone_ext: 'tax_collector_phone_ext',
  fax: 'tax_collector_fax',
  email: 'tax_collector_email',
  working_hours: 'tax_collector_working_hours',
};

/**
 * Read one canonical payee field from a jurisdiction, preferring the
 * Option-A `effective_payee` value when present. Empty strings are
 * treated as absent so a half-populated `effective_payee` cannot
 * blank out a populated legacy field.
 */
export function pickPayeeField(jurisdiction, payeeKey) {
  if (!jurisdiction) return '';
  const ep = jurisdiction.effective_payee;
  if (ep) {
    const v = ep[payeeKey];
    if (v !== null && v !== undefined && v !== '') return v;
  }
  const legacyKey = LEGACY_FIELD_BY_PAYEE_KEY[payeeKey];
  if (!legacyKey) return '';
  const lv = jurisdiction[legacyKey];
  return lv === null || lv === undefined ? '' : lv;
}

export function pickPayeeName(jurisdiction) {
  return pickPayeeField(jurisdiction, 'name');
}

export function pickPayeeMailingAddress(jurisdiction) {
  return pickPayeeField(jurisdiction, 'mailing_address');
}

export function pickPayeePhone(jurisdiction) {
  return pickPayeeField(jurisdiction, 'phone');
}

export function pickPayeePhoneExt(jurisdiction) {
  return pickPayeeField(jurisdiction, 'phone_ext');
}

export function pickPayeeEmail(jurisdiction) {
  return pickPayeeField(jurisdiction, 'email');
}

export function pickPayeeFax(jurisdiction) {
  return pickPayeeField(jurisdiction, 'fax');
}

export function pickPayeeMakeChecksPayableTo(jurisdiction) {
  return pickPayeeField(jurisdiction, 'make_checks_payable_to');
}

/**
 * Resolve the CHECK PAYABLE NAME for display surfaces (Jurisdiction
 * Directory column, linked-file payee blocks).
 *
 * Jul 2026 (PAYEE-CHECKNAME-001, Penn Forest/Carbon County UAT): when a
 * collector assignment exists, the payee is the collector's configured
 * `make_checks_payable_to` (a payment instruction), NOT its directory
 * `display_name`. `pickPayeeName` returned `effective_payee.name`
 * (= display_name, e.g. "PENN FOREST TOWNSHIP") which misrepresented
 * the payee ("CARBON COUNTY TREASURER"). Legacy fallback stays
 * `tax_collector_name` — the jurisdiction form's own Check Payable
 * Name field.
 */
export function pickCheckPayableName(jurisdiction) {
  if (!jurisdiction) return '';
  const ep = jurisdiction.effective_payee;
  if (ep) {
    const v = ep.make_checks_payable_to || ep.name;
    if (v !== null && v !== undefined && v !== '') return v;
  }
  // JURDIR-PAYEE-001 (Oct 2026): mirrors the backend resolver — stored payee first, legacy collector name as fallback.
  return jurisdiction.make_checks_payable_to
    || jurisdiction.tax_collector_name
    || '';
}

/**
 * True iff the jurisdiction's `effective_payee` was sourced from an
 * active collector assignment (Option-A path). Returns false for
 * legacy_fallback, missing field, or null/undefined input. Reserved
 * for future opt-in surfaces (e.g., a visual indicator on the admin
 * directory). P3.5 itself never renders any badge.
 */
export function isAssignmentSourced(jurisdiction) {
  return jurisdiction?.effective_payee?.source === 'assignment';
}
